import * as React from "react"
import CheckBox from "./com_ff-checkbox"
import Dropdown from "./com_ff-dropdown"
import Input from "./com_ff-input"
import TextArea from "./com_ff-textarea"
import Multilang from "./com_ff-multilang"
import Search from "./com_ff-search"
import { MultilangString } from "../../../models/product/multilang-string"
import {
  DropdownOptionRawValue,
  FieldDataType,
  FormFieldTypes,
  IDropdownOption,
  IFieldState,
} from "../../product-form/_reducers/_utils/product-form-types"
import { DropdownItemProps } from "semantic-ui-react"
import { Languages } from "../../../utils"
import { FieldType, IOldValue } from "../../../models"

export * from "./com_ff-blank"

// export type IFormFieldTypes = keyof typeof FormFieldTypes
// export const FormFieldTypes = {
//   Input: "input",
//   Number: "number",
//   Dropdown: "select",
//   Checkbox: "checkbox",
//   Blank: "placeholder",
//   Search: "search",
//   Textarea: "textArea",
//   Rate: "rate",
//   Date: "date",
// }

export interface IFieldValues {
  label: string
  id: number
}

export interface IBlankProps {
  simple?: boolean
  double?: boolean
  triple?: boolean
  style?: { lockDiv: object; textField: object }
}

export interface ICheckboxProps {
  checkboxStyle: "toggle" | "slider"
}

export interface IDropdownValues {
  label: string
  id: number
}

export interface IDropdownReadOnlyData {
  value: DropdownOptionRawValue
  label: string
  className: string
  onClick: () => void
}

export interface IDropdownProps {
  icon?: string
  type?: "CheckBox"
  multiple?: boolean
  className?: string
  allowAdditionsIfNoOptions?: boolean
  isObject?: boolean
  customPlaceholderOption?: IDropdownOption | IDropdownOption[]
  options: IDropdownOption[]
  readOnlyData?: IDropdownReadOnlyData[]
  loading?: boolean
  lazyload?: boolean
  style?: { globalDiv?: {}; div?: {}; input?: {}; label?: {} }
  searchInput?: string
  minCaracters?: number
}

export interface IMultilangProps {
  multiLine?: boolean
  size?: "mini" | "tiny" | "small" | "large" | "fullscreen"
}

export interface ITextareaProps {
  rows: number
}

export interface INumberProps {
  min?: number
  max?: number
  step?: number
}

export interface ISearchProps {
  options?: IDropdownOption[]
}

export interface IOnChangeProps {
  code: string
  fieldValue: string | MultilangString | string[]
  lock: "lock" | "unlock"
  fieldType: FieldType
  lang?: string
  overrideDefaultValue?: boolean
  forceAcceptEmptyArray?: boolean
  allowDefaultValue?: boolean
}

export interface IFormFieldProps<T extends FieldDataType> {
  data: IFieldState<T>
  oldValue?: IOldValue
  checkboxProps?: ICheckboxProps
  dropdownProps?: IDropdownProps & { labelClassName?: string }
  multilangProps?: IMultilangProps
  textareaProps?: ITextareaProps
  numberProps?: INumberProps
  searchProps?: ISearchProps
  formId?: string
  isNewProduct: boolean
  lockable: boolean
  label: any
  disabled?: boolean
  type?: string
  displayed?: boolean
  currency?: "euro" | "dollar"
  style?: { lockDiv?: object; textField?: object; text?: object }
  tabIndex?: number
  placeholder?: string
  lang?: Languages
  onclick?: () => void
  onfocus?: () => void
  onLockClick: (e: any, fieldType: FieldType) => void
  onModifyClick?: (e: any, fieldType: FieldType) => void
  onKeyDown?: (e: any) => void
  onKeyUp?: (e: any) => void
  onFieldLooseFocus: (field: IFieldState<T>) => void
  onChange: (props: IOnChangeProps) => void
}

const FormField = React.memo(
  <T extends FieldDataType>(props: IFormFieldProps<T>) => {
    const { displayed = true, data } = props
    if (!displayed) {
      return <section />
    }

    if (props) {
      switch (data.formFieldType) {
        case FormFieldTypes.Checkbox:
          // @ts-ignore
          return <CheckBox name={props.code} {...props} />

        case FormFieldTypes.Dropdown:
          // @ts-ignore
          return <Dropdown key={props.data.code} {...props} />

        case FormFieldTypes.Input:
          return data.localized ? (
            // @ts-ignore
            <Multilang {...props} type={"text"} />
          ) : (
            // @ts-ignore
            <Input {...props} type={"text"} />
          )

        case FormFieldTypes.Textarea:
          return props.data.localized ? (
            // @ts-ignore
            <Multilang
              {...props}
              multilangProps={Object.assign(
                { size: "large" },
                props.multilangProps
              )}
            />
          ) : (
            // @ts-ignore
            <TextArea {...props} />
          )

        case FormFieldTypes.Number:
          // @ts-ignore
          return <Input {...props} type={"number"} />

        case FormFieldTypes.Rate:
          return (
            // @ts-ignore
            <Input
              {...props}
              placeholder={"0.00"}
              type={"number"}
              numberProps={{ min: 0, max: 100, step: 0.01 }}
            />
          )

        case FormFieldTypes.Search:
          // @ts-ignore
          return <Search {...props} />

        default:
          // @ts-ignore
          return <Input {...props} type={"text"} />
      }
    }
    return null
  },
  (
    prevProps: Readonly<IFormFieldProps<FieldDataType>>,
    nextProps: Readonly<IFormFieldProps<FieldDataType>>
  ) => {
    return (
      JSON.stringify(prevProps, [
        "data",
        "checkboxProps",
        "dropdownProps",
        "multilangProps",
        "textareaProps",
        "numberProps",
        "searchProps",
        "formId",
        "lockable",
        "label",
        "disabled",
        "type",
        "displayed",
        "currency",
        "style",
        "tabIndex",
        "placeholder",
        "lang",
        // "onclick",
        // "onfocus",
        // "onLockClick",
        // "onModifyClick",
        // "onKeyDown",
        // "onKeyUp",
        // "onFieldLooseFocus",
        // "onChange",
      ]) ===
        JSON.stringify(nextProps, [
          "data",
          "checkboxProps",
          "dropdownProps",
          "multilangProps",
          "textareaProps",
          "numberProps",
          "searchProps",
          "formId",
          "lockable",
          "label",
          "disabled",
          "type",
          "displayed",
          "currency",
          "style",
          "tabIndex",
          "placeholder",
          "lang",
          // "onclick",
          // "onfocus",
          // "onLockClick",
          // "onModifyClick",
          // "onKeyDown",
          // "onKeyUp",
          // "onFieldLooseFocus",
          // "onChange",
        ]) &&
      prevProps.onclick === nextProps.onclick &&
      prevProps.onfocus === nextProps.onfocus &&
      prevProps.onLockClick === nextProps.onLockClick &&
      prevProps.onModifyClick === nextProps.onModifyClick &&
      prevProps.onKeyDown === nextProps.onKeyDown &&
      prevProps.onKeyUp === nextProps.onKeyUp &&
      prevProps.onFieldLooseFocus === nextProps.onFieldLooseFocus &&
      prevProps.onChange === nextProps.onChange
    )
  }
)

export default FormField
